import { ApiService } from "./apiService"; 
import { scratchData, networkData } from "./data";
// import { showManualPopup, shownNetworkErrorPopup, updateBalance } from "./UIManager";
// import { initializeGame, resumeGameFromPendingBet, startNewGame } from "./game"; 


export let serviceInitSuccess = false;

// Helper functions for checking data existence (similar to cashCashPabunot)
const hasTicketDetails = (data) => Object.keys(data?.data?.ticketDetails ?? {}).length > 0;
const hasBet = (data) => Object.keys(data?.data?.bet ?? {}).length > 0;
const hasNoBet = data => !hasBet(data);
const shouldPlaceBet = data => hasTicketDetails(data) && hasNoBet(data);
const previousBetNotCompleted = data => hasTicketDetails(data) && hasBet(data);


/**
 * Initializes the network service, including authentication and checking for pending bets.
 * This should be called once when your game starts.
 */
export const initializeNetworkService = async () => {
    try {
        serviceInitSuccess = true;

    } catch (error) {
        console.error("Error during network service initialization:", error);
        serviceInitSuccess = false;
        // showErrorPopup("Failed to initialize game. Please check your network connection.");
    }
};

/**
    * Fetches scores from the pending bet API.
    * @returns {Array<string>} An array of scores from the pending bet, or an empty array if none.
    */
export const getPendingBetScores = async () => {
    const defaultResponse = { scores: [], baseScorePattern: [] };
    if (!serviceInitSuccess) {
        console.error("Network service not initialized. Cannot check for pending bet scores.");
        return defaultResponse;
    }
   
    try {
        const pendingBetResponse = await ApiService.getLastPendingBet();
        networkData.dataLastPendingBet = pendingBetResponse.data;

        const pendingScores = pendingBetResponse.data?.bet?.scores || [];
        const basePattern = pendingBetResponse.data?.bet?.baseScorePattern || [];
        const scratchedIndexes = pendingBetResponse.data?.bet?.scratchedScoresIndexes || [];
        const walletBalance = pendingBetResponse.data?.wallet;

        if (pendingScores.length > 0) {
            console.log("Data retrieved from pending bet API:", { scores: pendingScores, baseScorePattern: basePattern, wallet: walletBalance });
            return { scores: pendingScores, baseScorePattern: basePattern, scratchedScoresIndexes: scratchedIndexes, wallet: walletBalance };
        }
        if (walletBalance !== undefined && walletBalance !== null) {
            return { ...defaultResponse, wallet: walletBalance };
        }
    } catch (error) {
    console.warn("No pending bet found or error fetching pending bet:", error);
    }
    return defaultResponse; 
};

/**
 * Handles the logic for buying tickets. This will be called when the Play button is clicked.
 * @param {object} betAmount The amount of the bet for buying tickets.
 */
export const handleBuyTickets = async (betAmount) => {
    if (!serviceInitSuccess) {
        console.error("Network service not initialized. Cannot buy tickets.");
        // shownNetworkErrorPopup();
        return { success: false, wallet: null };
    }

    try {
        console.log("Attempting to buy tickets with betAmount:", betAmount);
        const ticketDetails = {
            betAmount: betAmount,
            gameId: scratchData.gameId, 
            currencyId: 1,
        };
        const response = await ApiService.buyTickets(ticketDetails);
        console.log("Buy tickets API response:", response);

        // Process the response for buying tickets
        // Update networkData and scratchData based on the response
        networkData.buyTickets = response.data;
        //scratchData.balance = response.data?.wallet?.balance;
        const updatedWalletBalance = response.data?.wallet;
        // updateBalance(scratchData.balance); // Update UI balance
        return { success: true, wallet: updatedWalletBalance };;
        // You might then proceed to place a bet or update game state
        // await handlePlaceBet(); // If placing bet is a separate step after buying tickets
        
    } catch (error) {
        console.error("Failed to buy tickets:", error);
        // Handle specific errors for buying tickets (e.g., insufficient funds)
        if (error.response?.data.errors[0]?.description === "INSUFFICIENT_FUNDS") {
            // ShowInsufficientBalancePopup();
        } else if (error.message === "No authorization token.") {
            // showErrorPopup("Session expired. Please log in again.");
            scratchData.tokenExpired = true;
            // Trigger a re-login flow
        } else {
            // shownNetworkErrorPopup();
        }
        throw error;
    }
};

/**
 * Handles the logic for initiating the coin play game.
 */
export const handleCoinPlayGame = async (betAmount) => {
    const defaultResponse = { scores: [], baseScorePattern: [] };
    if (!serviceInitSuccess) {
        console.error("Network service not initialized. Cannot play game.");
        // shownNetworkErrorPopup();
        return defaultResponse;
    }

    try {
        console.log("Attempting to call Coin Play Game API...");
        // You'll need to generate a client seed (if not already done)
        const gameDetails = {
            gameId: scratchData.gameId,
            isCheatMode: false,
            cheatModeType: "none",
            currencyId: 1, 
        };
        const response = await ApiService.coinPlayGame(gameDetails);
        console.log("Coin Play Game API response:", response);

        // Process the response from the play game API
        // Update game state (e.g., networkData.gameResult = response.data.gameResult)
        networkData.dataScratchScore = response.data; // Store play game result

        const coinPlayScores = response.data?.details?.scores || [];
        const basePattern = response.data?.details?.baseScorePattern || [];
        if (coinPlayScores.length > 0) {
            console.log("Data retrieved from Coin Play Game API:", { scores: coinPlayScores, baseScorePattern: basePattern });
            return { scores: coinPlayScores, baseScorePattern: basePattern, didWin: networkData.overallGameDidWin };
        }
        return defaultResponse; // Return empty if scores are not found in the response

    } catch (error) {
        console.error("Failed to play game:", error);
        // Handle specific errors for playing game
        if (error.message === "No authorization token.") {
            // showErrorPopup("Session expired. Please log in again.");
            scratchData.tokenExpired = true;
            // Trigger a re-login flow
        } else {
            // shownNetworkErrorPopup();
        }
        throw error; // Re-throw for higher-level handling if needed
    }
};

/**
 * Handles the logic for scratching a symbol.
 * @param {number} index The index of the symbol being scratched.
 * @returns {Promise<object>} The response from the scratch-score API.
 */
export const handleScratchScore = async (index) => {
    if (!serviceInitSuccess) {
        console.error("Network service not initialized. Cannot scratch score.");
        throw new Error("Network service not initialized.");
    }

    try {
        console.log(`Attempting to scratch symbol at index: ${index}`);
        const response = await ApiService.scratchScore(index);
        console.log("Scratch Score API response:", response);
        return response;
    } catch (error) {
        console.error(`Failed to scratch symbol at index ${index}:`, error);
        throw error; 
    }
};

export const handleRevealAll = async () => {
    if (!serviceInitSuccess) {
        console.error("Network service not initialized. Cannot reveal.");
        throw new Error("Network service not initialized.");       
    }
    try {
        console.log("Attempting to reveal all scores via API...");
        const response = await ApiService.scratchCoinScore(0); 
        console.log("Reveal All API response:", response);
        return response;
    } catch (error) {
        console.error("Failed to reveal all scores:", error);
        throw error;
    }
};

export default initializeNetworkService;